import { BehaviorTree } from './BehaviorTree';
import { TaskStatus } from './TaskStatus';
import { Composite } from './composites/Composite';
import { Decorator } from './decorators/Decorator';
import { ExecuteAction } from './actions/ExecuteAction';
import { ExecuteActionConditional } from './conditionals/ExecuteActionConditional';
import { LogAction } from './actions/LogAction';
import { WaitAction } from './actions/WaitAction';
import { BehaviorTreeReference } from './actions/BehaviorTreeReference';
import { ConditionalDecorator } from './decorators/ConditionalDecorator';
import { AlwaysFail } from './decorators/AlwaysFail';
import { AlwaysSucceed } from './decorators/AlwaysSucceed';
import { Inverter } from './decorators/Inverter';
import { Repeater } from './decorators/Repeater';
import { UntilFail } from './decorators/UntilFail';
import { UntilSuccess } from './decorators/UntilSuccess';
import { Parallel } from './composites/Parallel';
import { ParallelSelector } from './composites/ParallelSelector';
import { Selector } from './composites/Selector';
import { RandomSelector } from './composites/RandomSelector';
import { Sequence } from './composites/Sequence';
import { RandomSequence } from './composites/RandomSequence';
import { AbortTypes } from './composites/AbortTypes';
import { Blackboard, BlackboardValueType } from './Blackboard';
/**
 * 行为树构建器类
 * @description 提供构建行为树的流畅API和配置加载功能
 * @template T 执行上下文类型
 *
 * @example
 * ```typescript
 * // 使用流畅API构建
 * const tree = BehaviorTreeBuilder.begin(context)
 *   .selector()
 *     .sequence()
 *       .logAction("开始执行")
 *       .waitAction(1.0)
 *     .endComposite()
 *     .logAction("备选方案")
 *   .endComposite()
 *   .build();
 *
 * // 从JSON配置构建
 * const result = BehaviorTreeBuilder.fromBehaviorTreeConfig(jsonConfig, context);
 * ```
 */
export class BehaviorTreeBuilder {
    /**
     * 构造函数
     * @param context 执行上下文
     */
    constructor(context) {
        /** 父节点堆栈，用于流畅API构建 */
        this._parentNodeStack = new Array();
        this._context = context;
    }
    /**
     * 开始构建行为树
     * @param context 执行上下文
     * @returns 新的构建器实例
     */
    static begin(context) {
        return new BehaviorTreeBuilder(context);
    }
    /**
     * 设置子节点到父节点
     * @param child 子节点
     * @returns 构建器实例
     */
    setChildOnParent(child) {
        const parent = this._parentNodeStack[this._parentNodeStack.length - 1];
        if (parent instanceof Composite) {
            parent.addChild(child);
        }
        else if (parent instanceof Decorator) {
            // 装饰器只有一个子节点，所以自动结束
            parent.child = child;
            this.endDecorator();
        }
        return this;
    }
    /**
     * 将节点推入父节点堆栈
     * @param composite 复合节点或装饰器节点
     * @returns 构建器实例
     */
    pushParentNode(composite) {
        if (this._parentNodeStack.length > 0) {
            this.setChildOnParent(composite);
        }
        this._parentNodeStack.push(composite);
        return this;
    }
    /**
     * 结束装饰器节点
     * @returns 构建器实例
     */
    endDecorator() {
        this._currentNode = this._parentNodeStack.pop();
        return this;
    }
    /**
     * 添加动作节点
     * @param func 动作执行函数
     * @returns 构建器实例
     */
    action(func) {
        if (this._parentNodeStack.length === 0) {
            throw new Error("无法创建无嵌套的动作节点，它必须是一个叶节点");
        }
        return this.setChildOnParent(new ExecuteAction(func));
    }
    /**
     * 添加返回布尔值的动作节点
     * @param func 返回布尔值的函数
     * @returns 构建器实例
     */
    actionR(func) {
        return this.action(t => func(t) ? TaskStatus.Success : TaskStatus.Failure);
    }
    /**
     * 添加条件节点
     * @param func 条件检查函数
     * @returns 构建器实例
     */
    conditional(func) {
        if (this._parentNodeStack.length === 0) {
            throw new Error("无法创建无嵌套的条件节点，它必须是一个叶节点");
        }
        return this.setChildOnParent(new ExecuteActionConditional(func));
    }
    /**
     * 添加返回布尔值的条件节点
     * @param func 返回布尔值的条件函数
     * @returns 构建器实例
     */
    conditionalR(func) {
        return this.conditional(t => func(t) ? TaskStatus.Success : TaskStatus.Failure);
    }
    /**
     * 添加日志动作节点
     * @param text 日志文本
     * @returns 构建器实例
     */
    logAction(text) {
        if (this._parentNodeStack.length === 0) {
            throw new Error("无法创建无嵌套的动作节点，它必须是一个叶节点");
        }
        return this.setChildOnParent(new LogAction(text));
    }
    /**
     * 添加等待动作节点
     * @param waitTime 等待时间（秒）
     * @returns 构建器实例
     */
    waitAction(waitTime) {
        if (this._parentNodeStack.length === 0) {
            throw new Error("无法创建无嵌套的动作节点，它必须是一个叶节点");
        }
        return this.setChildOnParent(new WaitAction(waitTime));
    }
    /**
     * 添加子行为树节点
     * @param subTree 子行为树实例
     * @returns 构建器实例
     */
    subTree(subTree) {
        if (this._parentNodeStack.length === 0) {
            throw new Error("无法创建无嵌套的动作节点，它必须是一个叶节点");
        }
        return this.setChildOnParent(new BehaviorTreeReference(subTree));
    }
    /**
     * 添加条件装饰器
     * @param func 条件函数
     * @param shouldReevaluate 是否重新评估
     * @returns 构建器实例
     */
    conditionalDecorator(func, shouldReevaluate = true) {
        const conditional = new ExecuteActionConditional(func);
        return this.pushParentNode(new ConditionalDecorator(conditional, shouldReevaluate));
    }
    /**
     * 添加返回布尔值的条件装饰器
     * @param func 返回布尔值的条件函数
     * @param shouldReevaluate 是否重新评估
     * @returns 构建器实例
     */
    conditionalDecoratorR(func, shouldReevaluate = true) {
        return this.conditionalDecorator(t => func(t) ? TaskStatus.Success : TaskStatus.Failure, shouldReevaluate);
    }
    /**
     * 添加总是失败装饰器
     * @returns 构建器实例
     */
    alwaysFail() {
        return this.pushParentNode(new AlwaysFail());
    }
    /**
     * 添加总是成功装饰器
     * @returns 构建器实例
     */
    alwaysSucceed() {
        return this.pushParentNode(new AlwaysSucceed());
    }
    /**
     * 添加反转装饰器
     * @returns 构建器实例
     */
    inverter() {
        return this.pushParentNode(new Inverter());
    }
    /**
     * 添加重复装饰器
     * @param count 重复次数
     * @returns 构建器实例
     */
    repeater(count) {
        return this.pushParentNode(new Repeater(count));
    }
    /**
     * 添加直到失败装饰器
     * @returns 构建器实例
     */
    untilFail() {
        return this.pushParentNode(new UntilFail());
    }
    /**
     * 添加直到成功装饰器
     * @returns 构建器实例
     */
    untilSuccess() {
        return this.pushParentNode(new UntilSuccess());
    }
    /**
     * 添加并行节点
     * @returns 构建器实例
     */
    paraller() {
        return this.pushParentNode(new Parallel());
    }
    /**
     * 添加并行选择器节点
     * @returns 构建器实例
     */
    parallelSelector() {
        return this.pushParentNode(new ParallelSelector());
    }
    /**
     * 添加选择器节点
     * @param abortType 中止类型
     * @returns 构建器实例
     */
    selector(abortType = AbortTypes.None) {
        return this.pushParentNode(new Selector(abortType));
    }
    /**
     * 添加随机选择器节点
     * @returns 构建器实例
     */
    randomSelector() {
        return this.pushParentNode(new RandomSelector());
    }
    /**
     * 添加序列节点
     * @param abortType 中止类型
     * @returns 构建器实例
     */
    sequence(abortType = AbortTypes.None) {
        return this.pushParentNode(new Sequence(abortType));
    }
    /**
     * 添加随机序列节点
     * @returns 构建器实例
     */
    randomSequence() {
        return this.pushParentNode(new RandomSequence());
    }
    /**
     * 结束复合节点
     * @returns 构建器实例
     */
    endComposite() {
        const topNode = this._parentNodeStack[this._parentNodeStack.length - 1];
        if (!(topNode instanceof Composite)) {
            throw new Error("尝试结束复合器，但顶部节点是装饰器");
        }
        this._currentNode = this._parentNodeStack.pop();
        return this;
    }
    /**
     * 构建最终的行为树
     * @param updatePeriod 更新周期（秒），默认0.2秒
     * @returns 构建好的行为树实例
     */
    build(updatePeriod = 0.2) {
        if (!this._currentNode) {
            throw new Error('无法创建零节点的行为树');
        }
        return new BehaviorTree(this._context, this._currentNode, updatePeriod);
    }
    /**
     * 从配置对象创建行为树
     * @param config 行为树配置
     * @param context 执行上下文
     * @returns 构建好的行为树
     */
    static fromConfig(config, context) {
        try {
            console.log('🌳 开始从配置创建行为树:', config);
            if (!config || !config.tree) {
                throw new Error('配置无效：缺少tree属性');
            }
            const rootNode = BehaviorTreeBuilder.createNodeFromConfig(config.tree);
            const updatePeriod = config.metadata?.updatePeriod ?? 0.2;
            console.log('✅ 行为树创建成功, 更新周期:', updatePeriod);
            return new BehaviorTree(context, rootNode, updatePeriod);
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error('❌ 从配置创建行为树失败:', error);
            throw new Error(`从配置创建行为树失败: ${errorMessage}`);
        }
    }
    /**
     * 从JSON配置创建行为树
     * @description 自动初始化黑板变量和构建节点树，提供一键式行为树创建
     * @param config JSON格式的行为树配置
     * @param context 执行上下文（可选，如果不提供将创建默认上下文）
     * @returns 包含行为树、黑板和增强上下文的结果对象
     *
     * @example
     * ```typescript
     * const config = {
     *   nodes: [...],
     *   blackboard: [...],
     *   metadata: { name: "MyBehaviorTree" }
     * };
     * const result = BehaviorTreeBuilder.fromBehaviorTreeConfig(config, context);
     * const { tree, blackboard, context: enhancedContext } = result;
     * ```
     */
    static fromBehaviorTreeConfig(config, context) {
        try {
            console.log('🌳 开始从配置创建行为树:', config.metadata?.name || '未命名');
            // 验证配置
            if (!config || !config.nodes || config.nodes.length === 0) {
                throw new Error('配置无效：缺少nodes属性或nodes为空');
            }
            // 创建黑板并初始化变量
            const blackboard = new Blackboard();
            if (config.blackboard && config.blackboard.length > 0) {
                console.log('📋 初始化黑板变量...');
                for (const variable of config.blackboard) {
                    // 映射类型字符串到枚举
                    const blackboardType = BehaviorTreeBuilder.mapToBlackboardType(variable.type);
                    blackboard.defineVariable(variable.name, blackboardType, variable.value, {
                        description: variable.description,
                        group: variable.group || 'Default',
                        readonly: variable.constraints?.readonly ?? false
                    });
                    console.log(`  ✅ ${variable.name}: ${variable.value} (${variable.type})`);
                }
            }
            // 创建或增强执行上下文
            const enhancedContext = (context || {});
            enhancedContext.blackboard = blackboard;
            // 构建节点树
            console.log('🔧 构建节点树...');
            const nodeMap = new Map();
            // 建立节点映射
            for (const node of config.nodes) {
                nodeMap.set(node.id, node);
            }
            // 找到根节点（通常是第一个节点或type为'root'的节点）
            const rootNodeConfig = config.nodes.find(n => n.type === 'root') || config.nodes[0];
            if (!rootNodeConfig) {
                throw new Error('未找到根节点');
            }
            // 递归构建节点树
            const rootNode = BehaviorTreeBuilder.createNodeFromJSONConfig(rootNodeConfig, nodeMap, enhancedContext);
            // 创建行为树
            const updatePeriod = config.metadata?.updatePeriod ?? 0.2;
            const tree = new BehaviorTree(enhancedContext, rootNode, updatePeriod, false, blackboard);
            console.log('✅ 行为树创建成功');
            console.log(`   📊 节点总数: ${config.nodes.length}`);
            console.log(`   📋 变量总数: ${config.blackboard?.length || 0}`);
            return { tree, blackboard, context: enhancedContext };
        }
        catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error('❌ 从配置创建行为树失败:', error);
            throw new Error(`从配置创建行为树失败: ${errorMessage}`);
        }
    }
    /**
     * 映射字符串类型到BlackboardValueType枚举
     * @param typeString 类型字符串
     * @returns 对应的黑板值类型枚举
     */
    static mapToBlackboardType(typeString) {
        switch (typeString.toLowerCase()) {
            case 'string':
                return BlackboardValueType.String;
            case 'number':
                return BlackboardValueType.Number;
            case 'boolean':
                return BlackboardValueType.Boolean;
            case 'vector2':
                return BlackboardValueType.Vector2;
            case 'vector3':
                return BlackboardValueType.Vector3;
            case 'object':
                return BlackboardValueType.Object;
            case 'array':
                return BlackboardValueType.Array;
            default:
                console.warn(`未知的变量类型: ${typeString}, 默认使用Object类型`);
                return BlackboardValueType.Object;
        }
    }
    /**
     * 从节点配置创建节点实例
     * @param nodeConfig 节点配置
     * @returns 创建的节点实例
     */
    static createNodeFromConfig(nodeConfig) {
        console.log('🔧 创建节点:', nodeConfig.type, nodeConfig.id);
        let node;
        // 根据节点类型创建对应的节点实例
        switch (nodeConfig.type) {
            // 复合节点
            case 'Sequence':
                const sequenceAbortValue = nodeConfig.properties?.abortType?.value;
                const sequenceAbortType = BehaviorTreeBuilder.getAbortType(typeof sequenceAbortValue === 'string' ? sequenceAbortValue : 'None');
                node = new Sequence(sequenceAbortType);
                break;
            case 'Selector':
                const selectorAbortValue = nodeConfig.properties?.abortType?.value;
                const selectorAbortType = BehaviorTreeBuilder.getAbortType(typeof selectorAbortValue === 'string' ? selectorAbortValue : 'None');
                node = new Selector(selectorAbortType);
                break;
            case 'Parallel':
                node = new Parallel();
                break;
            case 'ParallelSelector':
                node = new ParallelSelector();
                break;
            case 'RandomSelector':
                node = new RandomSelector();
                break;
            case 'RandomSequence':
                node = new RandomSequence();
                break;
            // 装饰器节点
            case 'AlwaysSucceed':
                node = new AlwaysSucceed();
                break;
            case 'AlwaysFail':
                node = new AlwaysFail();
                break;
            case 'Inverter':
                node = new Inverter();
                break;
            case 'Repeater':
                const countValue = nodeConfig.properties?.count?.value;
                const count = typeof countValue === 'number' ? countValue : 1;
                node = new Repeater(count);
                break;
            case 'UntilSuccess':
                node = new UntilSuccess();
                break;
            case 'UntilFail':
                node = new UntilFail();
                break;
            // 动作节点
            case 'LogAction':
                const messageValue = nodeConfig.properties?.message?.value;
                const message = typeof messageValue === 'string' ? messageValue : 'Default log message';
                node = new LogAction(message);
                break;
            case 'WaitAction':
                const waitTimeValue = nodeConfig.properties?.waitTime?.value;
                const waitTime = typeof waitTimeValue === 'number' ? waitTimeValue : 1.0;
                node = new WaitAction(waitTime);
                break;
            case 'ExecuteAction':
                // 对于自定义动作，我们创建一个默认的执行函数
                const actionCode = nodeConfig.properties?.actionCode?.value;
                if (actionCode && typeof actionCode === 'string') {
                    try {
                        // 简单的代码执行（在实际项目中应该更安全地处理）
                        const actionFunc = new Function('context', 'TaskStatus', `
                            const { Success, Failure, Running } = TaskStatus;
                            ${actionCode}
                        `);
                        node = new ExecuteAction((context) => {
                            try {
                                return actionFunc(context, TaskStatus);
                            }
                            catch (error) {
                                console.error('执行动作失败:', error);
                                return TaskStatus.Failure;
                            }
                        });
                    }
                    catch (error) {
                        console.warn('解析动作代码失败，使用默认动作:', error);
                        node = new ExecuteAction(() => TaskStatus.Success);
                    }
                }
                else {
                    node = new ExecuteAction(() => TaskStatus.Success);
                }
                break;
            default:
                console.warn('⚠️ 未知的节点类型:', nodeConfig.type, '，使用默认动作节点');
                node = new ExecuteAction(() => TaskStatus.Success);
                break;
        }
        // 为复合节点和装饰器添加子节点
        if (nodeConfig.children && nodeConfig.children.length > 0) {
            if (node instanceof Composite) {
                // 复合节点可以有多个子节点
                for (const childConfig of nodeConfig.children) {
                    const childNode = BehaviorTreeBuilder.createNodeFromConfig(childConfig);
                    node.addChild(childNode);
                }
            }
            else if (node instanceof Decorator) {
                // 装饰器只能有一个子节点
                if (nodeConfig.children.length > 1) {
                    console.warn('⚠️ 装饰器节点只能有一个子节点，将使用第一个');
                }
                const childNode = BehaviorTreeBuilder.createNodeFromConfig(nodeConfig.children[0]);
                node.child = childNode;
            }
        }
        console.log('✅ 节点创建完成:', nodeConfig.type);
        return node;
    }
    /**
     * 解析中止类型字符串为枚举值
     * @param value 中止类型字符串
     * @returns 对应的中止类型枚举值
     */
    static getAbortType(value) {
        switch (value) {
            case 'LowerPriority':
                return AbortTypes.LowerPriority;
            case 'Self':
                return AbortTypes.Self;
            case 'Both':
                return AbortTypes.Both;
            default:
                return AbortTypes.None;
        }
    }
    /**
     * 从JSON节点配置创建节点实例
     * @description 递归创建节点树，支持所有标准行为树节点类型
     * @param nodeConfig 当前节点配置
     * @param nodeMap 节点ID到配置的映射表
     * @param context 执行上下文
     * @returns 创建的节点实例
     */
    static createNodeFromJSONConfig(nodeConfig, nodeMap, context) {
        console.log('🔧 创建节点:', nodeConfig.type, nodeConfig.name);
        let node;
        const props = nodeConfig.properties || {};
        // 根据节点类型创建对应的节点实例
        switch (nodeConfig.type) {
            // 根节点 - 通常是一个简单的传递节点
            case 'root':
                // 根节点本身不执行逻辑，直接处理第一个子节点
                if (nodeConfig.children && nodeConfig.children.length > 0) {
                    const firstChildId = nodeConfig.children[0];
                    const firstChildConfig = nodeMap.get(firstChildId);
                    if (firstChildConfig) {
                        return BehaviorTreeBuilder.createNodeFromJSONConfig(firstChildConfig, nodeMap, context);
                    }
                }
                // 如果没有子节点，创建一个默认成功节点
                node = new ExecuteAction(() => TaskStatus.Success);
                break;
            // 复合节点
            case 'selector':
                node = new Selector(AbortTypes.None);
                break;
            case 'sequence':
                node = new Sequence(AbortTypes.None);
                break;
            case 'parallel':
                node = new Parallel();
                break;
            // 装饰器节点
            case 'repeater':
                const countProp = props.count;
                const count = typeof countProp === 'number' ? countProp : -1; // -1 表示无限重复
                node = new Repeater(count);
                break;
            case 'inverter':
                node = new Inverter();
                break;
            case 'conditional-decorator':
                // 创建条件装饰器
                const conditionFunc = BehaviorTreeBuilder.createConditionFunction(nodeConfig.condition, context);
                node = new ConditionalDecorator(new ExecuteActionConditional(conditionFunc), true);
                break;
            // 动作节点
            case 'log-action':
                const message = props.message || 'Default log message';
                // 支持变量替换
                node = new ExecuteAction((ctx) => {
                    const blackboard = ctx.blackboard;
                    let finalMessage = message;
                    // 简单的变量替换 {{variableName}}
                    if (blackboard && typeof message === 'string') {
                        finalMessage = message.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
                            const value = blackboard.getValue(varName);
                            return value !== undefined ? String(value) : match;
                        });
                    }
                    console.log(`[BehaviorTree] ${finalMessage}`);
                    if (ctx.log) {
                        ctx.log(finalMessage, props.logLevel || 'info');
                    }
                    return TaskStatus.Success;
                });
                break;
            case 'wait-action':
                const waitTimeProp = props.waitTime;
                const waitTime = typeof waitTimeProp === 'number' ? waitTimeProp : 1.0;
                node = new WaitAction(waitTime);
                break;
            case 'set-blackboard-value':
                const variableName = props.variableName;
                const value = props.value;
                node = new ExecuteAction((ctx) => {
                    const blackboard = ctx.blackboard;
                    if (blackboard && variableName) {
                        let finalValue = value;
                        // 支持变量替换
                        if (typeof value === 'string') {
                            finalValue = value.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
                                const val = blackboard.getValue(varName);
                                return val !== undefined ? String(val) : match;
                            });
                        }
                        blackboard.setValue(variableName, finalValue, props.force || false);
                        console.log(`[BehaviorTree] 设置变量 ${variableName} = ${finalValue}`);
                    }
                    return TaskStatus.Success;
                });
                break;
            case 'execute-action':
                const actionCode = props.actionCode;
                if (actionCode && typeof actionCode === 'string') {
                    try {
                        // 创建安全的执行函数
                        const actionFunc = new Function('context', 'TaskStatus', `
                            const { Success, Failure, Running, Invalid } = TaskStatus;
                            try {
                                ${actionCode}
                            } catch (error) {
                                console.error('动作执行错误:', error);
                                return TaskStatus.Failure;
                            }
                        `);
                        node = new ExecuteAction((ctx) => {
                            try {
                                const result = actionFunc(ctx, TaskStatus);
                                return result || TaskStatus.Success;
                            }
                            catch (error) {
                                console.error('执行动作失败:', error);
                                return TaskStatus.Failure;
                            }
                        });
                    }
                    catch (error) {
                        console.warn('解析动作代码失败，使用默认动作:', error);
                        node = new ExecuteAction(() => TaskStatus.Success);
                    }
                }
                else {
                    node = new ExecuteAction(() => TaskStatus.Success);
                }
                break;
            // 条件节点
            case 'condition-random':
                const probabilityProp = props.successProbability;
                const probability = typeof probabilityProp === 'number' ? probabilityProp : 0.5;
                node = new ExecuteActionConditional(() => {
                    return Math.random() < probability ? TaskStatus.Success : TaskStatus.Failure;
                });
                break;
            case 'condition-custom':
                const conditionCodeProp = props.conditionCode;
                const conditionCode = typeof conditionCodeProp === 'string' ? conditionCodeProp :
                    (typeof conditionCodeProp === 'object' && conditionCodeProp && 'value' in conditionCodeProp ?
                        String(conditionCodeProp.value) : undefined);
                if (conditionCode && typeof conditionCode === 'string') {
                    try {
                        const condFunc = new Function('context', `
                            try {
                                ${conditionCode}
                            } catch (error) {
                                console.error('条件检查错误:', error);
                                return false;
                            }
                        `);
                        node = new ExecuteActionConditional((ctx) => {
                            try {
                                const result = condFunc(ctx);
                                return result ? TaskStatus.Success : TaskStatus.Failure;
                            }
                            catch (error) {
                                console.error('条件检查失败:', error);
                                return TaskStatus.Failure;
                            }
                        });
                    }
                    catch (error) {
                        console.warn('解析条件代码失败:', error);
                        node = new ExecuteActionConditional(() => TaskStatus.Failure);
                    }
                }
                else {
                    node = new ExecuteActionConditional(() => TaskStatus.Success);
                }
                break;
            default:
                console.warn('⚠️ 未知的节点类型:', nodeConfig.type, '，使用默认成功节点');
                node = new ExecuteAction(() => TaskStatus.Success);
                break;
        }
        // 为复合节点和装饰器添加子节点
        if (nodeConfig.children && nodeConfig.children.length > 0) {
            if (node instanceof Composite) {
                // 复合节点可以有多个子节点
                for (const childId of nodeConfig.children) {
                    const childConfig = nodeMap.get(childId);
                    if (childConfig) {
                        const childNode = BehaviorTreeBuilder.createNodeFromJSONConfig(childConfig, nodeMap, context);
                        node.addChild(childNode);
                    }
                    else {
                        console.warn(`⚠️ 未找到子节点配置: ${childId}`);
                    }
                }
            }
            else if (node instanceof Decorator) {
                // 装饰器只能有一个子节点
                if (nodeConfig.children.length > 1) {
                    console.warn('⚠️ 装饰器节点只能有一个子节点，将使用第一个');
                }
                const childId = nodeConfig.children[0];
                const childConfig = nodeMap.get(childId);
                if (childConfig) {
                    const childNode = BehaviorTreeBuilder.createNodeFromJSONConfig(childConfig, nodeMap, context);
                    node.child = childNode;
                }
                else {
                    console.warn(`⚠️ 未找到子节点配置: ${childId}`);
                }
            }
        }
        console.log('✅ 节点创建完成:', nodeConfig.type);
        return node;
    }
    /**
     * 创建条件函数
     * @param condition 条件配置
     * @param context 执行上下文
     * @returns 条件检查函数
     */
    static createConditionFunction(condition, context) {
        if (!condition) {
            return () => TaskStatus.Success;
        }
        if (condition.type === 'condition-custom') {
            const conditionCodeConfig = condition.properties?.conditionCode;
            const conditionCode = typeof conditionCodeConfig === 'string' ? conditionCodeConfig :
                (typeof conditionCodeConfig === 'object' && conditionCodeConfig && 'value' in conditionCodeConfig ?
                    String(conditionCodeConfig.value) : undefined);
            if (conditionCode && typeof conditionCode === 'string') {
                try {
                    const condFunc = new Function('context', `
                        try {
                            return (${conditionCode})(context);
                        } catch (error) {
                            console.error('条件函数执行错误:', error);
                            return false;
                        }
                    `);
                    return (ctx) => {
                        try {
                            const result = condFunc(ctx);
                            return result ? TaskStatus.Success : TaskStatus.Failure;
                        }
                        catch (error) {
                            console.error('条件函数执行失败:', error);
                            return TaskStatus.Failure;
                        }
                    };
                }
                catch (error) {
                    console.warn('解析条件函数失败:', error);
                }
            }
        }
        return () => TaskStatus.Success;
    }
}
//# sourceMappingURL=BehaviorTreeBuilder.js.map